import { GlobalStyles, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material'
import { createContext, ReactNode, useContext, useEffect } from 'react'
import { useOmuiLdFlags } from './launch-darkly'
import { darkTheme, globalStyles, theme } from './theme'

// Merge into default ThemeProvider when theme2 is fully released
export const NewThemeProvider = ({ children, mode = 'light' }: { children: ReactNode; mode?: 'light' | 'dark' }) => {
  useEffect(() => {
    // Move to a top-level import when theme2 is fully released
    void import('@fontsource-variable/inter')
  }, [])

  return (
    <MuiThemeProvider theme={mode === 'dark' ? darkTheme : theme}>
      <GlobalStyles styles={globalStyles} />
      <ThemeSelectionContext.Provider value="v2">{children}</ThemeSelectionContext.Provider>
    </MuiThemeProvider>
  )
}

// Keeps a stable element structure across flag changes — branching between a
// fragment and a provider would remount the entire subtree when the LD flag resolves.
export const NewThemeProviderIfEnabled = ({ children }: { children: ReactNode }) => {
  const { RELEASE_OMUI_THEME2 } = useOmuiLdFlags()

  useEffect(() => {
    if (RELEASE_OMUI_THEME2) void import('@fontsource-variable/inter')
  }, [RELEASE_OMUI_THEME2])

  return (
    <MuiThemeProvider theme={(outerTheme: Theme) => (RELEASE_OMUI_THEME2 ? theme : outerTheme)}>
      {RELEASE_OMUI_THEME2 && <GlobalStyles styles={globalStyles} />}
      <ThemeSelectionContext.Provider value={RELEASE_OMUI_THEME2 ? 'v2' : 'v1'}>{children}</ThemeSelectionContext.Provider>
    </MuiThemeProvider>
  )
}

const ThemeSelectionContext = createContext<'v1' | 'v2'>('v1')
export const useThemeVersion = () => useContext(ThemeSelectionContext)
export const useIsThemeV2 = () => useThemeVersion() === 'v2'
